// src/pages/SelectCompanyPage.tsx
import { useEffect, useState } from "react";
import { apiGetJson } from "../utils/apiClient";
import { auth } from "../firebaseConfig";
import { apiGet } from "../utils/apiClient";
import { apiPost } from "../utils/apiClient";

function sanitizeRedirect(raw: string): string {
    const p = String(raw || "").trim();
    if (!p) return "";
    // 相対パスのみ許可（オープンリダイレクト対策）
    if (!p.startsWith("/") || p.startsWith("//")) return "";
    // ループ防止
    if (p.startsWith("/login") || p.startsWith("/check-status") || p.startsWith("/select-company")) return "";
    return p;
}

function consumePostLoginRedirect(): string {
    const p = sanitizeRedirect(localStorage.getItem("postLoginRedirect") || "");
    if (p) localStorage.removeItem("postLoginRedirect");
    return p;
}

function goTargetOrApp() {
    const redirect = consumePostLoginRedirect();
    if (redirect && !redirect.startsWith("/super")) {
        window.location.replace(redirect);
        return;
    }
    window.location.replace("/app");
}

export default function SelectCompanyPage() {
    const [shopUrl, setShopUrl] = useState("");
    const [submitting, setSubmitting] = useState(false);

    // 🔒 表示許可フラグ（これが全て）
    const [canRender, setCanRender] = useState(false);

    // -----------------------------
    // 初期判定（Auth + company）
    // -----------------------------
    useEffect(() => {
        const unsub = auth.onAuthStateChanged(async (user) => {
            if (!user) {
                console.error("unauthenticated access to SelectCompanyPage");
                window.location.replace("/");
                return;
            }
            try {
                // スーパーユーザー判定: /api/my-company の systemUser=true なら即 /super へ
                const data = await apiGetJson<any>("/api/my-company");
                if (data?.systemUser) {
                    window.location.replace("/super");
                    return;
                }
                if (data?.companyId) {
                    console.log("already registered company, redirecting...");
                    localStorage.setItem("companyId", data.companyId);
                    goTargetOrApp();
                    return;
                }
                setCanRender(true);
            } catch (e) {
                console.error("my-company check failed", e);

                // Account未登録でも super の場合は /super を優先
                try {
                    const meRes = await apiGet("/api/me");
                    if (meRes.ok) {
                        const me = await meRes.json().catch(() => ({}));
                        const isSuper =
                            me?.superAdmin === true ||
                            me?.superAdminGlobal === true ||
                            (Array.isArray(me?.superAdminTenants) && me.superAdminTenants.length > 0);
                        if (isSuper) {
                            // テナント専用superは許可テナントに切り替えてから /super へ
                            const superTenants: string[] = Array.isArray(me?.superAdminTenants) ? me.superAdminTenants : [];
                            if (!me?.superAdminGlobal && superTenants.length > 0) {
                                localStorage.setItem("selectedTenant", superTenants[0]);
                                sessionStorage.setItem("login_tenant", superTenants[0]);
                            }
                            window.location.replace("/super");
                            return;
                        }
                    }
                } catch {
                    // noop
                }

                setCanRender(true);
            }
        });
        return () => {
            unsub();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // -----------------------------
    // ❌ 判定が終わるまで一切描画しない
    // -----------------------------
    if (!canRender) return null;

    // -----------------------------
    // 登録処理
    // -----------------------------
    const handleRegister = async () => {
        if (!shopUrl) {
            alert("ショップURLを入力してください");
            return;
        }

        try {
            setSubmitting(true);

            const res = await apiPost("/api/join-company", { shopUrl });
            const data = await res.json();

            if (!res.ok) {
                // ショップが他テナントに登録済み → 該当テナントのログイン画面へ
                if (data?.error === "shop_registered_in_other_tenants") {
                    const targets: { tenantId: string; redirectPath: string }[] =
                        data?.redirectTargets ?? [];
                    const rp = (targets[0]?.redirectPath) || data?.redirectPath;
                    if (rp) {
                        window.location.replace(rp);
                        return;
                    }
                }
                alert(data?.message || "ショップ登録に失敗しました");
                return;
            }

            if (!data.success) {
                alert("ショップ登録に失敗しました");
                return;
            }

            // ★ company user
            if (data.companyId) {
                localStorage.setItem("companyId", data.companyId);

                // ✅ ここも最小修正：/app固定をやめる
                goTargetOrApp();
                return;
            }

            // ★ system user
            if (data.systemUser) {
                alert("スーパーユーザー用のアカウントを登録しました。管理者に連絡してください。");
                localStorage.removeItem("companyId"); // 明示的に
                window.location.replace("/");
                return;
            }

            // ここに来るのは想定外
            alert("不正な登録状態です");
        } catch (e) {
            console.error(e);
            alert("通信エラーが発生しました");
        } finally {
            setSubmitting(false);
        }
    };

    // -----------------------------
    // 登録画面
    // -----------------------------
    return (
        <div className="flex items-center justify-center min-h-screen bg-slate-50">
            <div className="bg-white p-6 rounded-lg shadow-md w-96">
                <h2 className="text-xl font-semibold mb-4">ショップURLを登録</h2>

                <input
                    className="w-full border p-3 mb-4 rounded text-sm"
                    placeholder="https://www.rakuten.co.jp/〇〇/"
                    value={shopUrl}
                    onChange={(e) => setShopUrl(e.target.value)}
                />

                <button
                    onClick={handleRegister}
                    disabled={submitting}
                    className="w-full bg-blue-600 text-white py-2 rounded hover:bg-blue-700 disabled:opacity-50"
                >
                    {submitting ? "登録中..." : "登録してダッシュボードへ"}
                </button>
            </div>
        </div>
    );
}
